import React, { useCallback, useEffect, useRef } from 'react';
import { CURRENT_ENV_ROLE_TYPE } from '../../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

const BUYER_PRIMARY = '#6027b0';
const SUPPLIER_PRIMARY = '#2792b0';
const GREY_COLOR = '#d9d9d9';
const BAR_WIDTH = 2;
const MIN_BAR_HEIGHT = 3;
const BAR_GAP = 2;
const FRAME_INTERVAL = 1000 / 20;

const getDefaultActiveColor = (): string => {
	if (CURRENT_ENV_ROLE_TYPE === ROLE_TYPES.BUYER) {
		return BUYER_PRIMARY;
	}
	return SUPPLIER_PRIMARY;
};

interface OWAudioWaveformProps {
	// Mode 1: Live recording
	stream?: MediaStream | null;
	isPaused?: boolean;
	playbackProgress?: number;

	// Mode 2: Playback
	data?: number[];
	progress?: number;

	// Shared
	activeColor?: string;
	inactiveColor?: string;
	onClick?: (percent: number) => void;
}

const BAR_RADIUS = 2;

const drawRoundedRect = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number
) => {
	const r = Math.min(BAR_RADIUS, width / 2, height / 2);
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.lineTo(x + width - r, y);
	ctx.arcTo(x + width, y, x + width, y + r, r);
	ctx.lineTo(x + width, y + height - r);
	ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
	ctx.lineTo(x + r, y + height);
	ctx.arcTo(x, y + height, x, y + height - r, r);
	ctx.lineTo(x, y + r);
	ctx.arcTo(x, y, x + r, y, r);
	ctx.closePath();
	ctx.fill();
};

const getBarCount = (canvasWidth: number): number => {
	return Math.floor(canvasWidth / (BAR_WIDTH + BAR_GAP));
};

const drawBars = (
	ctx: CanvasRenderingContext2D,
	barHeights: number[],
	canvasWidth: number,
	canvasHeight: number,
	color: string,
	secondaryColor?: string,
	progressX?: number
) => {
	ctx.clearRect(0, 0, canvasWidth, canvasHeight);

	for (let i = 0; i < barHeights.length; i++) {
		const barHeight = Math.max(barHeights[i], MIN_BAR_HEIGHT);
		const x = i * (BAR_WIDTH + BAR_GAP);
		const y = (canvasHeight - barHeight) / 2;

		if (secondaryColor && progressX !== undefined) {
			ctx.fillStyle = x + BAR_WIDTH <= progressX ? color : secondaryColor;
		} else {
			ctx.fillStyle = color;
		}

		drawRoundedRect(ctx, x, y, BAR_WIDTH, barHeight);
	}
};

const OWAudioWaveform: React.FC<OWAudioWaveformProps> = ({
	stream,
	isPaused,
	playbackProgress,
	data,
	progress,
	activeColor,
	inactiveColor,
	onClick,
}) => {
	const resolvedActiveColor = activeColor || getDefaultActiveColor();
	const resolvedInactiveColor = inactiveColor || GREY_COLOR;

	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const audioContextRef = useRef<AudioContext | null>(null);
	const lastDrawTimeRef = useRef<number>(0);
	const amplitudeHistoryRef = useRef<number[]>([]);
	const pauseDrawnRef = useRef(false);
	const isPausedRef = useRef(isPaused);
	isPausedRef.current = isPaused;
	const playbackProgressRef = useRef(playbackProgress || 0);
	playbackProgressRef.current = playbackProgress || 0;
	const isLiveMode = !!stream;

	// Live recording mode — rolling amplitude timeline
	useEffect(() => {
		if (!stream) {
			return;
		}

		amplitudeHistoryRef.current = [];
		pauseDrawnRef.current = false;

		try {
			const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
			audioContextRef.current = audioContext;

			const source = audioContext.createMediaStreamSource(stream);
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 256;
			analyserRef.current = analyser;

			source.connect(analyser);

			const bufferLength = analyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);

			const updateWaveform = () => {
				const now = Date.now();

				if (canvasRef.current) {
					const canvas = canvasRef.current;
					const ctx = canvas.getContext('2d');

					if (ctx) {
						if (isPausedRef.current) {
							if (amplitudeHistoryRef.current.length > 0) {
								const pbProgress = playbackProgressRef.current;
								if (pbProgress > 0) {
									const progressX = canvas.width * pbProgress;
									drawBars(
										ctx,
										amplitudeHistoryRef.current,
										canvas.width,
										canvas.height,
										resolvedActiveColor,
										GREY_COLOR,
										progressX
									);
									pauseDrawnRef.current = false;
								} else if (!pauseDrawnRef.current) {
									drawBars(
										ctx,
										amplitudeHistoryRef.current,
										canvas.width,
										canvas.height,
										GREY_COLOR
									);
									pauseDrawnRef.current = true;
								}
							}
						} else if (now - lastDrawTimeRef.current >= FRAME_INTERVAL && analyserRef.current) {
							pauseDrawnRef.current = false;
							analyserRef.current.getByteTimeDomainData(dataArray);

							// Compute RMS amplitude from time-domain data
							let sumOfSquares = 0;
							for (let i = 0; i < bufferLength; i++) {
								const normalized = (dataArray[i] - 128) / 128;
								sumOfSquares += normalized * normalized;
							}
							const rms = Math.sqrt(sumOfSquares / bufferLength);

							// Convert RMS to bar height using log scale for better dynamic range
							let barHeight;
							if (rms < 0.01) {
								barHeight = MIN_BAR_HEIGHT;
							} else {
								// Log scale: maps ~0.01-0.5 RMS to ~10%-100% of canvas height
								const logScale =
									(Math.log(rms + 0.01) - Math.log(0.01)) / (Math.log(0.5) - Math.log(0.01));
								barHeight = Math.min(logScale * canvas.height, canvas.height);
							}
							barHeight = Math.max(barHeight, MIN_BAR_HEIGHT);

							// Push to rolling buffer, trim to bar count
							const barCount = getBarCount(canvas.width);
							amplitudeHistoryRef.current.push(barHeight);
							const excess = amplitudeHistoryRef.current.length - barCount;
							if (excess > 0) {
								amplitudeHistoryRef.current.splice(0, excess);
							}

							drawBars(
								ctx,
								amplitudeHistoryRef.current,
								canvas.width,
								canvas.height,
								resolvedActiveColor
							);
							lastDrawTimeRef.current = now;
						}
					}
				}

				animationFrameRef.current = requestAnimationFrame(updateWaveform);
			};

			updateWaveform();
		} catch (error) {
			console.error('Error initializing audio analysis:', error);
		}

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			analyserRef.current = null;
		};
	}, [stream, resolvedActiveColor]);

	// Playback mode
	useEffect(() => {
		if (isLiveMode || !data || !canvasRef.current) {
			return;
		}

		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}

		const barHeights = data.map((value) => (value / 100) * canvas.height);
		const progressX = progress !== undefined ? canvas.width * progress : 0;

		drawBars(
			ctx,
			barHeights,
			canvas.width,
			canvas.height,
			resolvedActiveColor,
			resolvedInactiveColor,
			progressX
		);
	}, [data, progress, isLiveMode, resolvedActiveColor, resolvedInactiveColor]);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			if (!onClick || !canvasRef.current) {
				return;
			}
			const rect = canvasRef.current.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const percent = x / rect.width;
			onClick(Math.max(0, Math.min(1, percent)));
		},
		[onClick]
	);

	return (
		<canvas
			ref={canvasRef}
			width={300}
			height={40}
			className={'block h-full w-full' + (onClick ? ' cursor-pointer' : '')}
			onClick={onClick ? handleClick : undefined}
		/>
	);
};

export default OWAudioWaveform;
